/**
 * Service functions for handling simulation control during match events
 */

export function normalizeLiveCommandAttemptId(value) {
  if (value === undefined || value === null) {
    return null;
  }
  const normalized = String(value).trim();
  return normalized.length > 0 ? normalized : null;
}

export function buildLiveCommandAttemptData(engineBootAttemptId) {
  const normalizedAttemptId = normalizeLiveCommandAttemptId(engineBootAttemptId);
  return normalizedAttemptId ? { engineBootAttemptId: normalizedAttemptId } : {};
}

/**
 * Pause game for a user action
 * 
 * @param {Object} params Configuration parameters
 * @param {string} params.matchId Match ID for safe backend routing
 * @param {string} params.reason Reason for pausing ('substitution' or 'tactic_change')
 * @param {string} [params.engineBootAttemptId] Current live command attempt marker
 * @param {Object} params.socketCtx Socket context for sending commands
 * @param {Function} params.onSuccess Success callback
 * @param {Function} params.onError Error callback
 * @returns {Promise<boolean>} Success status
 */
export async function pauseGame({
  matchId,
  reason,
  engineBootAttemptId,
  socketCtx,
  onSuccess,
  onError
}) {
  console.log('🔥🔥🔥 PAUSEGAME CALLED - REBUILD TEST 🔥🔥🔥');
  console.log('[pauseGame] Function called with:', { reason, socketCtx: !!socketCtx, onSuccess: typeof onSuccess, onError: typeof onError });
  
  console.log('🚨 CRITICAL DEBUG: About to check socketCtx...');
  
  if (!socketCtx) {
    console.error('❌ socketCtx is falsy:', socketCtx);
    onError?.("WebSocket connection not available. Cannot pause game.");
    return false;
  }
  
  console.log('✅ socketCtx exists, checking sendControlSimulation...');
  
  if (!socketCtx.sendControlSimulation) {
    console.error('❌ socketCtx.sendControlSimulation is not available');
    onError?.("WebSocket sendControlSimulation method not available. Cannot pause game.");
    return false;
  }

  if (!matchId) {
    console.error('[pauseGame] Missing matchId for pause command.');
    onError?.("Match ID missing. Cannot pause game safely.");
    return false;
  }
  
  console.log('🎯 About to call socketCtx.sendControlSimulation(PAUSE)...');
  
  const ACK_TIMEOUT_MS = 7000;

  // Send pause command WITH REASON so backend/engine can propagate it.
  // Treat acks as the source of truth; emitting successfully is not enough.
  return await new Promise((resolve) => {
    let timeoutId = null;
    let settled = false;

    const finish = (success, message, details) => {
      if (settled) return;
      settled = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (success) onSuccess?.(message);
      else if (details !== undefined) onError?.(message, details);
      else onError?.(message);
      resolve(!!success);
    };

    timeoutId = setTimeout(() => {
      console.error('[pauseGame] Timed out waiting for backend ack.');
      finish(false, 'Timed out pausing the game');
    }, ACK_TIMEOUT_MS);

    const emitted = socketCtx.sendControlSimulation('PAUSE', {
      matchId,
      reason,
      ...buildLiveCommandAttemptData(engineBootAttemptId)
    }, (ackResponse) => {
      console.log('[pauseGame] Ack response received from backend for PAUSE:', ackResponse);
      if (ackResponse?.success) {
        finish(true, `Game paused for ${reason}`);
      } else {
        finish(false, ackResponse?.error || 'Failed to pause the game', ackResponse);
      }
    });

    console.log('🎯 sendControlSimulation returned:', emitted);

    if (!emitted) {
      console.error('❌ Failed to emit pause command');
      finish(false, 'Failed to pause the game');
    }
  });
}

/**
 * Resume game after a user action
 * 
 * @param {Object} params Configuration parameters
 * @param {string} params.matchId Match ID for safe backend routing
 * @param {string} [params.engineBootAttemptId] Current live command attempt marker
 * @param {string} [params.injuryResolution] How an injury pause should be resolved ('play_on').
 *   Omitted for every other resume: the engine then picks its own safe default.
 * @param {Object} params.socketCtx Socket context for sending commands
 * @param {Function} params.onSuccess Success callback
 * @param {Function} params.onError Error callback
 * @returns {Promise<boolean>} Success status
 */
export async function resumeGame({
  matchId,
  engineBootAttemptId,
  injuryResolution,
  socketCtx,
  onSuccess,
  onError
}) {
  console.log('🔥🔥🔥 RESUMEGAME CALLED 🔥🔥🔥');
  console.log('[resumeGame] Function called with:', { socketCtx: !!socketCtx, onSuccess: typeof onSuccess, onError: typeof onError });

  if (!socketCtx || !socketCtx.sendControlSimulation) {
    console.error('[resumeGame] socketCtx or sendControlSimulation not available');
    onError?.("WebSocket connection not available. Cannot resume game.");
    return false;
  }

  if (!matchId) {
    console.error('[resumeGame] Missing matchId for resume command.');
    onError?.("Match ID missing. Cannot resume game safely.");
    return false;
  }

  console.log('[resumeGame] About to call socketCtx.sendControlSimulation(RESUME)...');

  const ACK_TIMEOUT_MS = 7000;

  // Treat acks as the source of truth; emitting successfully is not enough.
  return await new Promise((resolve) => {
    let timeoutId = null;
    let settled = false;

    const finish = (success, message, details) => {
      if (settled) return;
      settled = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (success) onSuccess?.(message);
      else if (details !== undefined) onError?.(message, details);
      else onError?.(message);
      resolve(!!success);
    };

    timeoutId = setTimeout(() => {
      console.error('[resumeGame] Timed out waiting for backend ack.');
      finish(false, 'Timed out resuming the game');
    }, ACK_TIMEOUT_MS);

    const emitted = socketCtx.sendControlSimulation('RESUME', {
      matchId,
      ...buildLiveCommandAttemptData(engineBootAttemptId),
      ...(injuryResolution ? { injury_resolution: injuryResolution } : {})
    }, (ackResponse) => {
      console.log('[resumeGame] Ack response received from backend for RESUME:', ackResponse);
      if (ackResponse?.success) {
        finish(true, 'Game resumed');
      } else {
        finish(false, ackResponse?.error || 'Failed to resume the game', ackResponse);
      }
    });

    console.log('[resumeGame] sendControlSimulation returned:', emitted);

    if (!emitted) {
      finish(false, 'Failed to resume the game');
    }
  });
}
